import { useState, useRef, useCallback } from 'react';
import {
  Upload, FileText, CheckCircle, AlertCircle, X, Download,
  ChevronRight, Info, Loader2, Table, AlertTriangle, RefreshCw
} from 'lucide-react';
import apiClient from '@/services/api/apiClient';

interface Props {
  onImported: (batchId: string) => void;
  onClose: () => void;
}

interface RowError { row: number; company: string; error: string; }
interface ImportResult {
  batchId: string;
  imported: number;
  newCompanies: number;
  duplicates: number;
  skipped: number;
  rowErrors?: RowError[];
  columnMapping?: Record<string, string>;
  message: string;
}

// ── Template CSV ──────────────────────────────────────────────────────────────
const TEMPLATE = `accountType,companyName,domain,region,estimatedSize,industryVertical,primaryPainAxis,maturityBand,icpScore,partnerType,partnerTerritory,partnerMarketFocus,partnerPortfolio,keyContactName,keyContactEmail,keyContactPhone,keyContactTitle,keyContactDept,keyContactLinkedIn,signals,architectureTags,winTheme
BUYER,Apex Forge Industries,apexforge.in,India,200-500 employees,Manufacturing,QUALITY,L3,82,,,,,Rajesh Mehta,rajesh@apexforge.in,+91-9876543210,VP Operations,Operations,https://linkedin.com/in/rajesh,Quality recall reported|ISO audit due,SAP|SCADA|IoT,OEE optimisation for precision components
BUYER,Gulf Steel Works,gulfsteel.ae,UAE,500-1000 employees,Steel & Metals,VISIBILITY,L2,76,,,,,Ahmed Al-Rashid,,+971-501234567,Plant Director,Operations,,No real-time OEE|New plant director hired,Wonderware|legacy-systems,Unified plant floor visibility for steel operations
PARTNER,Meridian Tech Solutions,meridiantech.my,Malaysia,100-250 employees,,,,74,SYSTEM_INTEGRATOR,Southeast Asia,Discrete & automotive,SCADA/PLC integration|Oracle ERP resale,Priya Nair,priya@meridiantech.my,,CTO,Technology,https://linkedin.com/in/priya,Ignition SI partner|Siemens automation reseller,Oracle|hybrid-cloud,Complementary MES line for their SI practice`;

// ── Supported column reference ─────────────────────────────────────────────
const COLUMN_DOCS = [
  { field: 'companyName *',       aliases: 'Company Name, Account, Organisation',         required: true  },
  { field: 'region *',            aliases: 'Country, Location, Geography, Market',         required: true  },
  { field: 'accountType',         aliases: 'Type — BUYER (default) or PARTNER',           required: false },
  { field: 'industryVertical',    aliases: 'Industry, Sector, Vertical — required for BUYER rows', required: false },
  { field: 'domain',              aliases: 'Website, URL, Web, Homepage',                  required: false },
  { field: 'estimatedSize',       aliases: 'Employees, Headcount, Company Size, Staff',    required: false },
  { field: 'primaryPainAxis',     aliases: 'Pain Axis (BUYER) — VISIBILITY | EXECUTION | DATA | QUALITY | INTEGRATION', required: false },
  { field: 'maturityBand',        aliases: 'Maturity (BUYER) — L1 | L2 | L3 | L4 | L5',  required: false },
  { field: 'partnerType',         aliases: 'PARTNER — RESELLER | SYSTEM_INTEGRATOR | DISTRIBUTOR | VAR | CONSULTANT', required: false },
  { field: 'partnerTerritory',    aliases: 'PARTNER — Territory / coverage served',        required: false },
  { field: 'partnerMarketFocus',  aliases: 'PARTNER — Market / vertical focus',           required: false },
  { field: 'partnerPortfolio',    aliases: 'PARTNER — What they sell / integrate today',   required: false },
  { field: 'icpScore',            aliases: 'ICP Score, Fit Score, Lead Score (0–100)',     required: false },
  { field: 'keyContactName',      aliases: 'Contact Name, POC, Decision Maker',           required: false },
  { field: 'keyContactEmail',     aliases: 'Contact Email, Email, Work Email',             required: false },
  { field: 'keyContactPhone',     aliases: 'Contact Phone, Phone, Mobile, Direct Dial',   required: false },
  { field: 'keyContactTitle',     aliases: 'Contact Title, Job Title, Position, Role',    required: false },
  { field: 'keyContactDept',      aliases: 'Department, Dept, Function, Team',            required: false },
  { field: 'keyContactLinkedIn',  aliases: 'LinkedIn, LinkedIn URL, Profile URL',         required: false },
  { field: 'signals',             aliases: 'Buying Signals, Intent Signals — separate with | or ;', required: false },
  { field: 'architectureTags',    aliases: 'Tech Stack, Technologies, Systems, Platforms — separate with ,', required: false },
  { field: 'winTheme',            aliases: 'Win Theme, Pitch, Value Proposition',         required: false },
];

type Step = 'upload' | 'preview' | 'done';

export default function CsvSeedUpload({ onImported, onClose }: Props) {
  const [step, setStep]           = useState<Step>('upload');
  const [csvText, setCsvText]     = useState('');
  const [label, setLabel]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState<ImportResult | null>(null);
  const [error, setError]         = useState('');
  const [dragOver, setDragOver]   = useState(false);
  const [showColDocs, setShowColDocs] = useState(false);
  const [fileName, setFileName]   = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Quote-aware CSV line parser (handles commas inside "..." ) ───────────
  const parseCsvLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === ',' && !inQ) { out.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };

  // ── Preview parsed rows from csvText ─────────────────────────────────────
  const previewRows = (() => {
    if (!csvText.trim()) return [];
    const lines = csvText.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = parseCsvLine(lines[0]).map(h => h.replace(/^"|"$/g, ''));
    return lines.slice(1, 6).map(line => {
      const vals = parseCsvLine(line);
      return Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']));
    });
  })();

  const rowCount = csvText.trim()
    ? csvText.trim().split('\n').filter(l => l.trim()).length - 1
    : 0;

  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string || '';
      setCsvText(text);
      setStep('preview');
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.txt'))) handleFile(file);
    else setError('Please drop a .csv file');
  };

  const handleSubmit = async () => {
    if (!csvText.trim()) { setError('No CSV data to import'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post('/api/prospecting/seed-csv', {
        csvData: csvText,
        batchLabel: label || undefined,
      });
      setResult(data);
      setStep('done');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.hint || err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'cantier-prospect-import-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const S = {
    input: 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors',
    label: 'block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Table className="h-4.5 w-4.5 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">Import from CSV</h2>
              <p className="text-xs text-muted-foreground">Seed prospects directly — bypasses AI discovery</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Step indicator */}
            {['upload','preview','done'].map((s, i) => (
              <div key={s} className="flex items-center gap-1">
                <div className={`h-1.5 w-6 rounded-full transition-colors ${
                  step === s ? 'bg-primary' :
                  (['upload','preview','done'].indexOf(step) > i) ? 'bg-primary/40' : 'bg-muted'
                }`} />
              </div>
            ))}
            <button onClick={onClose} className="ml-2 p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1">

          {/* ══ STEP 1: Upload ══ */}
          {step === 'upload' && (
            <div className="p-5 space-y-4">
              {/* Actions row */}
              <div className="flex items-center justify-between">
                <button onClick={downloadTemplate}
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors">
                  <Download className="h-3.5 w-3.5" />
                  Download template (.csv)
                </button>
                <button onClick={() => setShowColDocs(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <Info className="h-3.5 w-3.5" />
                  {showColDocs ? 'Hide' : 'Show'} supported columns
                </button>
              </div>

              {/* Column docs */}
              {showColDocs && (
                <div className="rounded-xl border border-border bg-muted/30 overflow-hidden">
                  <div className="px-3 py-2 border-b border-border bg-muted/50">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Supported columns — your header names are auto-mapped
                    </p>
                  </div>
                  <div className="divide-y divide-border max-h-48 overflow-y-auto">
                    {COLUMN_DOCS.map(col => (
                      <div key={col.field} className="flex items-start gap-3 px-3 py-1.5 text-xs">
                        <code className={`shrink-0 font-mono font-medium px-1 py-0.5 rounded text-[10px] ${
                          col.required ? 'bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400' : 'bg-muted text-foreground'
                        }`}>{col.field}</code>
                        <span className="text-muted-foreground">{col.aliases}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Batch label */}
              <div>
                <label className={S.label}>Batch Label (optional)</label>
                <input type="text" className={S.input} value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. Q3 2026 Manufacturing — India pipeline" />
              </div>

              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  dragOver
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-muted/20'
                }`}>
                <Upload className={`h-8 w-8 mx-auto mb-3 transition-colors ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
                <p className="text-sm font-medium text-foreground">Drop your CSV file here</p>
                <p className="text-xs text-muted-foreground mt-1">or click to browse · .csv files only</p>
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden"
                  onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or paste directly</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Paste area */}
              <div>
                <label className={S.label}>Paste CSV</label>
                <textarea value={csvText}
                  onChange={e => { setCsvText(e.target.value); if (e.target.value.trim()) setStep('preview'); }}
                  placeholder="companyName,domain,region,industryVertical..."
                  rows={5}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none" />
                {rowCount > 0 && (
                  <p className="text-xs text-primary mt-1 font-medium">{rowCount} rows detected</p>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                  <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ══ STEP 2: Preview ══ */}
          {step === 'preview' && (
            <div className="p-5 space-y-4">
              {/* Summary bar */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{fileName || 'Pasted CSV'}</span>
                  <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">{rowCount} rows</span>
                </div>
                <button onClick={() => { setCsvText(''); setFileName(''); setStep('upload'); }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <RefreshCw className="h-3 w-3" />Change file
                </button>
              </div>

              {/* Batch label */}
              <div>
                <label className={S.label}>Batch Label</label>
                <input type="text" className={S.input} value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder={`CSV import ${new Date().toLocaleDateString('en-IN')}`} />
              </div>

              {/* Data preview table */}
              {previewRows.length > 0 && (
                <div>
                  <label className={S.label}>Preview (first {previewRows.length} rows)</label>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          {Object.keys(previewRows[0]).slice(0, 6).map(h => (
                            <th key={h} className="px-2 py-2 text-left font-semibold text-muted-foreground truncate max-w-[120px]">{h}</th>
                          ))}
                          {Object.keys(previewRows[0]).length > 6 && (
                            <th className="px-2 py-2 text-muted-foreground">+{Object.keys(previewRows[0]).length - 6} more</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {previewRows.map((row, i) => (
                          <tr key={i} className="hover:bg-muted/30">
                            {Object.values(row).slice(0, 6).map((v, j) => (
                              <td key={j} className="px-2 py-1.5 truncate max-w-[120px] text-foreground" title={String(v)}>
                                {String(v) || <span className="text-muted-foreground/50">—</span>}
                              </td>
                            ))}
                            {Object.values(row).length > 6 && <td className="px-2 py-1.5 text-muted-foreground">…</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {rowCount > 5 && (
                    <p className="text-xs text-muted-foreground mt-1">Showing 5 of {rowCount} rows</p>
                  )}
                </div>
              )}

              {/* Info notice */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-400">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Column headers are auto-mapped (case-insensitive). Companies already in the portal are detected
                  and will be upserted (not duplicated) when you approve prospects.
                </span>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                  <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-red-600 dark:text-red-400">
                    <p className="font-medium">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ STEP 3: Done ══ */}
          {step === 'done' && result && (
            <div className="p-5 space-y-4">
              {/* Success header */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Import complete</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-0.5">{result.message}</p>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Imported',    value: result.imported,     color: 'text-foreground'   },
                  { label: 'New',         value: result.newCompanies,  color: 'text-primary'      },
                  { label: 'Duplicates',  value: result.duplicates,    color: 'text-amber-600 dark:text-amber-400'  },
                  { label: 'Skipped',     value: result.skipped,       color: 'text-red-500'      },
                ].map(s => (
                  <div key={s.label} className="rounded-lg border border-border bg-muted/30 p-3 text-center">
                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wide">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Column mapping */}
              {result.columnMapping && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Column mapping</p>
                  <div className="rounded-xl border border-border overflow-hidden">
                    <div className="grid grid-cols-2 text-xs divide-y divide-border max-h-36 overflow-y-auto">
                      {Object.entries(result.columnMapping).map(([raw, mapped]) => (
                        <div key={raw} className="contents">
                          <div className="px-3 py-1.5 bg-muted/30 font-mono text-muted-foreground truncate">{raw}</div>
                          <div className={`px-3 py-1.5 font-medium truncate ${
                            mapped === '(unmapped)' ? 'text-muted-foreground/50 italic' : 'text-foreground'
                          }`}>→ {mapped}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Row errors */}
              {result.rowErrors && result.rowErrors.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    {result.rowErrors.length} rows skipped
                  </p>
                  <div className="rounded-xl border border-amber-200 dark:border-amber-800 overflow-hidden divide-y divide-amber-100 dark:divide-amber-900 max-h-36 overflow-y-auto">
                    {result.rowErrors.map((e, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-950/20 text-xs">
                        <span className="text-amber-600 dark:text-amber-400 font-medium shrink-0">Row {e.row}</span>
                        <span className="text-amber-700 dark:text-amber-300 font-semibold shrink-0">{e.company}</span>
                        <span className="text-amber-600 dark:text-amber-400">{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.duplicates > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    {result.duplicates} companies already exist in the portal.
                    When you approve them, their data will be upserted (merged) — not duplicated.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer buttons ── */}
        <div className="p-5 border-t border-border shrink-0 flex gap-2">
          {step === 'upload' && (
            <>
              <button onClick={() => { if (csvText.trim()) setStep('preview'); }}
                disabled={!csvText.trim()}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 transition-colors">
                Preview <ChevronRight className="h-4 w-4" />
              </button>
              <button onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors">
                Cancel
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button onClick={handleSubmit} disabled={loading}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 transition-colors">
                {loading
                  ? <><Loader2 className="h-4 w-4 animate-spin" />Importing {rowCount} rows…</>
                  : <><Upload className="h-4 w-4" />Import {rowCount} prospects</>}
              </button>
              <button onClick={() => setStep('upload')} disabled={loading}
                className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted disabled:opacity-40 transition-colors">
                Back
              </button>
            </>
          )}
          {step === 'done' && (
            <>
              <button onClick={() => result && onImported(result.batchId)}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                Review prospects →
              </button>
              <button onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors">
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
